import React, { useCallback, useMemo, useState } from "react";
import { Button, Table } from "reactstrap";
import cx from "classnames";
import { ProductSuggestionTableRow } from "./ProductSuggestionTableItem";

import styles from "../../../ProductConfigurationResultMainView.module.scss";
import { Icon } from "components/Icon/Icon";
import { useSubscription } from "urql";
import { resultingListSubscription } from "api/subscription/product";
import {
  useProductOutput,
  useProductInput,
  useProductUnit,
} from "stores/product";
import { useResultingListForm } from "../../providers/ResultingListProvider";
import { Spinner, SPINNER_SIZES } from "components/Spinner";
import { useDirtyCheck } from "./useDirtyCheck";
import { ExpandableSection } from "../../../ExpandableSection";
import { useTranslate } from "hooks";
import { useLanguage } from "stores/globalStore";
import { useUser } from "stores/user";
import type { VariablesOf } from "gql.tada";

type ResultingListSubscriptionVariables = VariablesOf<
  typeof resultingListSubscription
>;

export const SuggestionTable = () => {
  const [isFetching, setIsFetching] = useState(false);
  const productOutput = useProductOutput();
  const productInput = useProductInput();
  const unit = useProductUnit();
  const { values } = useResultingListForm();
  const user = useUser();

  const translate = useTranslate();

  const lang = useLanguage();

  const targetFilterValues = useMemo(() => {
    return Object.entries(values)
      .filter(([_, value]) => value !== false)
      .map(([id, value]) => {
        const handleValue = () => {
          if (typeof value === "boolean") {
            return value ? "true" : "false";
          }
          if (typeof value === "number") {
            return String(value);
          }
          if (!value) {
            return value;
          }
          return value;
        };
        const val = handleValue();
        return {
          id,
          value: val,
        };
      });
  }, [values]);

  const strategyId = productOutput?.product?.currentStrategyId ?? "";

  const variables: ResultingListSubscriptionVariables = useMemo(
    () => ({
      input: {
        seriesId: productInput?.seriesId,
        strategyId: strategyId,
        state: productInput?.state?.orderCodeState,
        targetFilterValues,
        languageIso2L: lang,
        countryIso2L: user?.tenant,
        requestUnitsSystem: unit,
        resultUnitsSystem: unit,
      } as ResultingListSubscriptionVariables["input"],
    }),
    [
      productInput?.seriesId,
      productInput?.state,
      strategyId,
      targetFilterValues,
      lang,
      user?.tenant,
      unit,
    ],
  );

  const [{ data }, executeSubscription] = useSubscription(
    {
      query: resultingListSubscription,
      variables,
      pause: true,
    },
    (_messages, response) => {
      setIsFetching(false);
      return response;
    },
  );

  const { isDirty, markExecuted } = useDirtyCheck({
    ...variables.input,
    state: null,
  });

  const handleGetList = useCallback(() => {
    setIsFetching(true);
    markExecuted();
    executeSubscription();
  }, [executeSubscription, markExecuted]);

  const productListViewItems = useMemo(() => {
    const productListView = data?.productListView;
    if (productListView && "listViewData" in productListView) {
      return productListView.listViewData.productListViewItems ?? [];
    }
    return [];
  }, [data]);

  const columnTitles = useMemo(() => {
    const productListView = data?.productListView;
    if (productListView && "listViewData" in productListView) {
      return productListView.listViewData.columnTitles ?? [];
    }
    return [];
  }, [data]);

  const columnHeaderTitles = useMemo(() => {
    return productOutput?.product?.listViewConfiguration?.columnTitles ?? [];
  }, [productOutput]);

  const errorMessage = useMemo(() => {
    if (data?.productListView && "message" in data.productListView) {
      return data.productListView.message;
    }
    return null;
  }, [data]);

  return (
    <ExpandableSection
      title=""
      classNames={cx(
        styles.acousticCell,
        "border rounded p-2 m-2 m-xxl-3 mt-xxl-0 p-xxl-2",
      )}
      viewWithTable={true}
    >
      {({ isInModal }: { isInModal: boolean }) => (
        <div className="px-3">
          <div className="w-100">
            {!isInModal && (
              <Button
                className="mt-n6 px-4 position-relative"
                color="outline-dark"
                onClick={handleGetList}
                disabled={isFetching}
                id="ProductSuggestionResultTable_calculate-btn"
              >
                <Icon iconName="arrow-rotate-left-regular" className="me-2" />
                {translate("PRODUCT_SUGGESTION_CALCULATE_BTN")}
                {isFetching && (
                  <span className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center">
                    <Spinner size={SPINNER_SIZES.XS} />
                  </span>
                )}
              </Button>
            )}
            <div className="w-100 overflow-auto">
              <Table
                className={styles.table}
                id="ProductSuggestionResultTable_table"
              >
                <thead
                  className={cx({ "opacity-50": !data || isDirty })}
                  id="ProductSuggestionResultTable_thead"
                >
                  <tr>
                    {columnHeaderTitles.map(
                      (column) =>
                        column && (
                          <th
                            key={column.key}
                            id={`ProductSuggestionResultTable_header-${column.key}`}
                          >
                            {column.title}
                          </th>
                        ),
                    )}
                  </tr>
                </thead>
                {errorMessage && (
                  <div id="ProductSuggestionResultTable_error-msg">
                    {errorMessage}
                  </div>
                )}
                {data && !isDirty && !isFetching && (
                  <tbody id="ProductSuggestionResultTable_tbody">
                    {productListViewItems.map((item, key: number) => (
                      <ProductSuggestionTableRow
                        key={key}
                        listItem={item}
                        currentRowKey={key}
                        columnTitles={columnTitles}
                      />
                    ))}
                  </tbody>
                )}
              </Table>
            </div>
          </div>
        </div>
      )}
    </ExpandableSection>
  );
};
